import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { TrendingUp, LogOut, LayoutDashboard, Users, Coins, Shield, FileText, Wallet, Download, Upload, BarChart3, ChevronDown, Zap, Repeat, Bell, MessageSquare, Settings, DollarSign, Menu, X, Rocket, Bot } from 'lucide-react';
import toast from 'react-hot-toast';

const AdminNavbar = () => {
    const { logout } = useAuthStore();
    const location = useLocation();
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
    };

    // Grouped Navigation Structure
    const navGroups = {
        dashboard: { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
        users: { path: '/admin/users', label: 'Users', icon: Users },
        trades: {
            label: 'Trades',
            icon: BarChart3,
            items: [
                { path: '/admin/trades/spot', label: 'Spot Trades', icon: BarChart3 },
                { path: '/admin/trades/futures', label: 'Futures', icon: Zap },
                { path: '/admin/trades/p2p', label: 'P2P Trading', icon: Repeat },
                { path: '/admin/bot-trading', label: 'Spot Bot', icon: Bot },
                { path: '/admin/futures-bot', label: 'Futures Bot', icon: Zap },
            ]
        },
        financial: {
            label: 'Financial',
            icon: DollarSign,
            items: [
                { path: '/admin/deposits', label: 'Deposits', icon: Download },
                { path: '/admin/withdrawals', label: 'Withdrawals', icon: Upload },
                { path: '/admin/balance-management', label: 'Balance Mgmt', icon: Wallet },
                { path: '/admin/tokens', label: 'Tokens', icon: Coins },
                { path: '/admin/token-launch', label: 'Token Launch', icon: Rocket },
                { path: '/admin/reports', label: 'Reports', icon: BarChart3 },
            ]
        },
        management: {
            label: 'Management',
            icon: FileText,
            items: [
                { path: '/admin/kyc-requests', label: 'KYC Requests', icon: FileText },
                { path: '/admin/announcements', label: 'Announcements', icon: Bell },
                { path: '/admin/support', label: 'Support Tickets', icon: MessageSquare },
            ]
        },
        settings: {
            label: 'Settings',
            icon: Settings,
            items: [
                { path: '/admin/settings', label: 'General', icon: Settings },
                { path: '/admin/settings/fees', label: 'Fee Settings', icon: DollarSign },
                { path: '/admin/settings/referrals', label: 'Referrals', icon: Users },
                { path: '/admin/binance-settings', label: 'Binance API', icon: Zap },
                { path: '/admin/settings/security', label: 'Security', icon: Shield },
                { path: '/admin/wallet-settings', label: 'Wallets', icon: Wallet },
                { path: '/admin/manage-admins', label: 'Admins', icon: Shield },
            ]
        }
    };

    const isGroupActive = (group) => {
        if (group.path) return location.pathname === group.path;
        return group.items?.some(item => location.pathname === item.path);
    };

    const renderDropdown = (key, group) => {
        const Icon = group.icon;
        const isOpen = activeDropdown === key;
        const isActive = isGroupActive(group);

        return (
            <div
                key={key}
                className="relative"
                onMouseEnter={() => setActiveDropdown(key)}
                onMouseLeave={() => setActiveDropdown(null)}
            >
                <button className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive
                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                    }`}>
                    <Icon size={16} />
                    <span>{group.label}</span>
                    <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                    <div className="absolute top-full left-0 mt-1 w-52 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl py-2 z-50 animate-fadeIn">
                        {group.items.map((item) => {
                            const ItemIcon = item.icon;
                            const itemActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-150 ${itemActive
                                        ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500'
                                        : 'text-gray-400 hover:text-white hover:bg-slate-700/50 hover:pl-5'
                                        }`}
                                >
                                    <ItemIcon size={16} />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderNavLink = (key, item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
            <Link
                key={key}
                to={item.path}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive
                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                    }`}
            >
                <Icon size={16} />
                <span>{item.label}</span>
            </Link>
        );
    };

    return (
        <nav className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700/50 sticky top-0 z-50 shadow-lg">
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex justify-between items-center h-14">
                    {/* Logo */}
                    <Link to="/admin" className="flex items-center gap-2 group">
                        <div className="p-1.5 bg-blue-600/20 rounded-lg group-hover:bg-blue-600/30 transition-colors">
                            <TrendingUp className="text-blue-500" size={20} />
                        </div>
                        <span className="text-lg font-bold text-white hidden sm:block">Admin</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-1">
                        {renderNavLink('dashboard', navGroups.dashboard)}
                        {renderNavLink('users', navGroups.users)}
                        {renderDropdown('trades', navGroups.trades)}
                        {renderDropdown('financial', navGroups.financial)}
                        {renderDropdown('management', navGroups.management)}
                        {renderDropdown('settings', navGroups.settings)}
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center gap-2">
                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden p-2 text-gray-400 hover:text-white rounded-lg hover:bg-slate-700/50"
                        >
                            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>

                        {/* Profile Button */}
                        <Link
                            to="/admin/profile"
                            className="flex items-center gap-2 text-gray-400 hover:text-white px-3 py-2 rounded-lg hover:bg-slate-700/50 transition-all duration-200"
                        >
                            <div className="p-1 bg-blue-500/10 rounded-full">
                                <Users size={16} className="text-blue-400" />
                            </div>
                            <span className="hidden sm:inline text-sm font-medium">Profile</span>
                        </Link>

                        {/* Logout Button */}
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 text-gray-400 hover:text-red-400 px-3 py-2 rounded-lg hover:bg-red-500/10 transition-all duration-200 group"
                        >
                            <LogOut size={18} className="group-hover:rotate-12 transition-transform" />
                            <span className="hidden sm:inline text-sm font-medium">Logout</span>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="lg:hidden pb-4 border-t border-slate-700/50 mt-2 pt-4">
                        <div className="space-y-1">
                            <Link to="/admin" className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white rounded-lg hover:bg-slate-700/50">
                                <LayoutDashboard size={18} /> Dashboard
                            </Link>
                            <Link to="/admin/users" className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white rounded-lg hover:bg-slate-700/50">
                                <Users size={18} /> Users
                            </Link>
                            {Object.entries(navGroups).filter(([_, g]) => g.items).map(([key, group]) => (
                                <div key={key} className="pt-2">
                                    <p className="px-3 text-xs text-gray-500 uppercase tracking-wider">{group.label}</p>
                                    {group.items.map(item => {
                                        const ItemIcon = item.icon;
                                        return (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white rounded-lg hover:bg-slate-700/50 ml-2"
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                <ItemIcon size={16} /> {item.label}
                                            </Link>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.15s ease-out;
                }
            `}</style>
        </nav>
    );
};

export default AdminNavbar;
